import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, CheckCircle, AlertCircle, GraduationCap } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getNextSchoolYear = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  // Current school year
  const startYear = month >= 9 ? year : year - 1;
  // Next school year
  return `${startYear + 1}-${startYear + 2}`;
};

export default function NewSchoolYearWizard({ open, onClose, token, onComplete }) {
  const [schoolYear, setSchoolYear] = useState(getNextSchoolYear());
  const [promotions, setPromotions] = useState([]); // [{old_class_id, old_class_name, new_class_name, students:[{user_id,name,promote}], expanded}]
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState('configure'); // 'configure' | 'done'
  const [doneResults, setDoneResults] = useState(null);

  // Load classes + students when dialog opens
  useEffect(() => {
    if (!open) return;
    setStep('configure');
    setDoneResults(null);
    loadData();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const classesRes = await axios.get(`${API_URL}/classes`, { headers });
      const classes = classesRes.data;

      // Fetch students for each class in parallel
      const studentsPerClass = await Promise.all(
        classes.map(c =>
          axios.get(`${API_URL}/classes/${c.class_id}/students`, { headers })
            .then(r => ({ class_id: c.class_id, students: r.data }))
            .catch(() => ({ class_id: c.class_id, students: [] }))
        )
      );
      const studentMap = {};
      studentsPerClass.forEach(({ class_id, students }) => {
        studentMap[class_id] = students;
      });

      const proms = classes.map(c => ({
        old_class_id: c.class_id,
        old_class_name: c.name,
        old_school_year: c.school_year,
        new_class_name: c.name, // pre-fill with same name, admin edits
        students: (studentMap[c.class_id] || []).map(s => ({
          user_id: s.user_id,
          name: s.name,
          promote: true // default: all promoted
        })),
        expanded: false,
        include: true // include this class in the rollover
      }));

      setPromotions(proms);
    } catch {
      toast.error('Грешка при зареждане на данните');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (idx) => {
    setPromotions(prev => prev.map((p, i) => i === idx ? { ...p, expanded: !p.expanded } : p));
  };

  const toggleInclude = (idx) => {
    setPromotions(prev => prev.map((p, i) => i === idx ? { ...p, include: !p.include } : p));
  };

  const setNewName = (idx, name) => {
    setPromotions(prev => prev.map((p, i) => i === idx ? { ...p, new_class_name: name } : p));
  };

  const toggleStudent = (classIdx, studentIdx) => {
    setPromotions(prev => prev.map((p, i) => {
      if (i !== classIdx) return p;
      const students = p.students.map((s, si) =>
        si === studentIdx ? { ...s, promote: !s.promote } : s
      );
      return { ...p, students };
    }));
  };

  const toggleAllStudents = (classIdx, promote) => {
    setPromotions(prev => prev.map((p, i) => {
      if (i !== classIdx) return p;
      return { ...p, students: p.students.map(s => ({ ...s, promote })) };
    }));
  };

  const handleSubmit = async () => {
    if (!schoolYear.trim()) {
      toast.error('Въведете учебна година (напр. 2026-2027)');
      return;
    }
    const included = promotions.filter(p => p.include && p.students.length > 0);
    if (included.length === 0) {
      toast.error('Изберете поне един клас с ученици');
      return;
    }
    for (const p of included) {
      if (!p.new_class_name.trim()) {
        toast.error(`Въведете ново име за клас "${p.old_class_name}"`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        school_year: schoolYear.trim(),
        promotions: included.map(p => ({
          old_class_id: p.old_class_id,
          new_class_name: p.new_class_name.trim(),
          students: p.students.map(s => ({ student_id: s.user_id, promote: s.promote }))
        }))
      };
      const res = await axios.post(`${API_URL}/admin/new-school-year`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDoneResults(res.data);
      setStep('done');
      // Refresh data in background after done step renders
      if (onComplete) setTimeout(onComplete, 150);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Грешка при прилагане');
    } finally {
      setSubmitting(false);
    }
  };

  const includedCount = promotions.filter(p => p.include).length;
  const totalStudents = promotions.filter(p => p.include).reduce((sum, p) => sum + p.students.length, 0);
  const promotedCount = promotions.filter(p => p.include).reduce((sum, p) => sum + p.students.filter(s => s.promote).length, 0);
  const heldBackCount = totalStudents - promotedCount;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <GraduationCap className="h-5 w-5 text-[#00966E]" />
            Нова учебна година
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-12 text-center text-slate-500">Зареждане на класове и ученици...</div>
        )}

        {!loading && step === 'configure' && (
          <div className="space-y-5">
            {/* School year input */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800 mb-3">
                <strong>Как работи:</strong> За всеки клас ще се създаде нов клас за новата учебна година.
                Повишените ученици ще бъдат прехвърлени в новия клас. Задържаните остават в стария клас.
                Историческите данни (присъствия, уроци, плащания) <strong>не се изтриват</strong>.
              </p>
              <div className="flex items-center gap-3">
                <Label htmlFor="school-year" className="whitespace-nowrap font-semibold">Нова учебна година:</Label>
                <Input
                  id="school-year"
                  data-testid="school-year-input"
                  value={schoolYear}
                  onChange={e => setSchoolYear(e.target.value)}
                  placeholder="напр. 2026-2027"
                  className="max-w-[160px] font-mono"
                />
              </div>
            </div>

            {/* Summary bar */}
            {promotions.length > 0 && (
              <div className="flex gap-4 text-sm bg-slate-50 border rounded-lg px-4 py-2.5 flex-wrap">
                <span className="text-slate-600">Класове: <strong>{includedCount}</strong></span>
                <span className="text-green-700">Повишени: <strong>{promotedCount}</strong></span>
                <span className="text-orange-600">Задържани: <strong>{heldBackCount}</strong></span>
              </div>
            )}

            {/* Class list */}
            {promotions.map((promo, idx) => (
              <Card key={promo.old_class_id} className={`border-2 transition-colors ${promo.include ? 'border-slate-200' : 'border-slate-100 opacity-60'}`}>
                {/* Class header */}
                <div className="p-4">
                  <div className="flex items-center gap-3 flex-wrap">
                    {/* Include checkbox */}
                    <input
                      type="checkbox"
                      checked={promo.include}
                      onChange={() => toggleInclude(idx)}
                      data-testid={`include-class-${idx}`}
                      className="h-4 w-4 accent-[#00966E] cursor-pointer"
                    />
                    {/* Current class name */}
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-semibold text-slate-800">{promo.old_class_name}</span>
                      {promo.old_school_year && (
                        <Badge className="text-xs bg-slate-100 text-slate-600 font-normal">{promo.old_school_year}</Badge>
                      )}
                      <span className="text-slate-400 text-sm">→</span>
                    </div>
                    {/* New class name input */}
                    <Input
                      value={promo.new_class_name}
                      onChange={e => setNewName(idx, e.target.value)}
                      placeholder="Ново име на клас"
                      disabled={!promo.include}
                      data-testid={`new-class-name-${idx}`}
                      className="max-w-[220px] text-sm h-8"
                    />
                    {/* School year badge */}
                    {promo.include && schoolYear && (
                      <Badge className="text-xs bg-green-100 text-green-700">{schoolYear}</Badge>
                    )}
                    {/* Student count + expand */}
                    <button
                      type="button"
                      onClick={() => toggleExpand(idx)}
                      className="ml-auto flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
                      data-testid={`expand-class-${idx}`}
                    >
                      <span>{promo.students.length} ученика</span>
                      {promo.expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Students list (expandable) */}
                {promo.expanded && (
                  <div className="border-t px-4 pb-4">
                    {promo.students.length === 0 ? (
                      <p className="text-slate-400 text-sm py-3">Няма ученици в този клас.</p>
                    ) : (
                      <>
                        {/* Select all / none */}
                        <div className="flex gap-3 py-2 text-xs">
                          <button
                            type="button"
                            onClick={() => toggleAllStudents(idx, true)}
                            className="text-green-600 hover:underline font-medium"
                          >
                            Избери всички
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleAllStudents(idx, false)}
                            className="text-orange-600 hover:underline font-medium"
                          >
                            Задържи всички
                          </button>
                        </div>
                        <div className="divide-y">
                          {promo.students.map((student, si) => (
                            <div key={student.user_id} className="flex items-center justify-between py-2">
                              <span className="text-sm text-slate-700">{student.name}</span>
                              <button
                                type="button"
                                onClick={() => toggleStudent(idx, si)}
                                data-testid={`student-toggle-${idx}-${si}`}
                                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                                  student.promote
                                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                    : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                                }`}
                              >
                                {student.promote ? (
                                  <><CheckCircle className="h-3 w-3" /> Повишен</>
                                ) : (
                                  <><AlertCircle className="h-3 w-3" /> Задържан (остава)</>
                                )}
                              </button>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </Card>
            ))}

            {promotions.length === 0 && !loading && (
              <p className="text-slate-400 text-center py-8">Няма налични класове.</p>
            )}

            {/* Action buttons */}
            <div className="flex gap-3 justify-end pt-2 border-t">
              <Button variant="outline" onClick={onClose} disabled={submitting}>Отказ</Button>
              <Button
                data-testid="apply-new-year-btn"
                onClick={handleSubmit}
                disabled={submitting || includedCount === 0}
                className="bg-[#00966E] hover:bg-[#007a59] text-white"
              >
                {submitting ? 'Прилагане...' : `Приложи учебна ${schoolYear}`}
              </Button>
            </div>
          </div>
        )}

        {/* Done step */}
        {step === 'done' && doneResults && (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <CheckCircle className="h-10 w-10 text-green-600 mx-auto mb-2" />
              <p className="font-semibold text-green-800 text-lg">
                Учебна година {doneResults.school_year} е приложена успешно!
              </p>
              <p className="text-green-700 text-sm mt-1">
                Създадени {doneResults.classes_created} нови класа
              </p>
            </div>
            <div className="space-y-2">
              {doneResults.results.map((r, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border text-sm">
                  <div>
                    <span className="text-slate-500">{r.old_class_name}</span>
                    <span className="text-slate-400 mx-2">→</span>
                    <span className="font-semibold text-slate-800">{r.new_class_name}</span>
                    <Badge className="ml-2 text-xs bg-green-100 text-green-700">{doneResults.school_year}</Badge>
                  </div>
                  <div className="flex gap-3 text-xs">
                    <span className="text-green-600">↑ {r.promoted} повишени</span>
                    {r.held_back > 0 && <span className="text-orange-500">⟳ {r.held_back} задържани</span>}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end pt-2 border-t">
              <Button onClick={onClose} className="bg-[#00966E] hover:bg-[#007a59] text-white">
                Затвори
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
